import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Gallery.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Gallery() {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [slideOffset, setSlideOffset] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const fetchGalleryImages = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gallery`);
      if (response.ok) {
        const data = await response.json();
        setImages(Array.isArray(data) ? data : []);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGalleryImages();
  }, [fetchGalleryImages]);

  useEffect(() => {
    // Get container width for swipe calculations
    const container = document.querySelector('.gallery-fullscreen');
    if (container) {
      setContainerWidth(container.clientWidth);
    }
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  const handleImageClick = (image) => {
    if (image.product_id) {
      navigate(`/product/${image.product_id}`);
    } else if (image.external_url) {
      window.open(image.external_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length > 0) {
      setTouchStartX(e.touches[0].clientX);
      setSlideOffset(0);
      setIsSliding(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isSliding || !containerWidth) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - touchStartX;
    setSlideOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isSliding) return;
    setIsSliding(false);

    if (!containerWidth) {
      setSlideOffset(0);
      return;
    }

    // Threshold is 20% of container width or 50px, whichever is smaller
    const threshold = Math.min(containerWidth * 0.2, 50);

    // slideOffset positive = swiped right (go to previous)
    // slideOffset negative = swiped left (go to next)
    if (slideOffset > threshold) {
      goToPrevious();
    } else if (slideOffset < -threshold) {
      goToNext();
    }

    setSlideOffset(0);
  };

  if (loading) {
    return (
      <div className="gallery-fullscreen">
        <div className="gallery-loading">Loading gallery...</div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="gallery-fullscreen">
        <div className="gallery-empty">No images available in gallery.</div>
      </div>
    );
  }

  return (
    <div
      className="gallery-fullscreen"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="gallery-viewer">
        {/* Images Container with Sliding Animation */}
        <div
          className="gallery-images-container"
          style={{
            transform: `translateX(${-currentIndex * 100 + (slideOffset / (containerWidth || 1)) * 100}%)`,
            transition: isSliding ? 'none' : 'transform 0.3s ease-out'
          }}
        >
          {images.map((image, idx) => (
            <div
              key={image.id || idx}
              className="gallery-image-wrapper"
              onClick={() => handleImageClick(image)}
              role={(image.product_id || image.external_url) ? 'button' : undefined}
              tabIndex={(image.product_id || image.external_url) ? 0 : undefined}
              onKeyPress={(e) => (image.product_id || image.external_url) && (e.key === 'Enter' || e.key === ' ') && handleImageClick(image)}
            >
              <img
                src={image.image_url}
                alt={`Gallery ${idx + 1}`}
                className="gallery-image"
              />
              {idx === currentIndex && image.product_id && (
                <div className="gallery-click-overlay">
                  <span className="gallery-click-text">Click to view product</span>
                </div>
              )}
              {idx === currentIndex && image.external_url && (
                <div className="gallery-click-overlay">
                  <span className="gallery-click-text">Click to visit link</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Close/Back Button */}
        <button
          className="gallery-close-btn"
          onClick={() => window.history.back()}
          title="Close gallery"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
